import { defineStore } from 'pinia';
import { MOBILE_VIEW_THRESHOLD } from '@/common/lib/constant';

export type LeadView = 'table' | 'cards';

interface TableSettings {
  [key: string]: {
    recordsPerPage: number;
  };
}

const MIN_RECORD: number = 10;

interface UIState {
  leadView: LeadView;
  tableSettings: TableSettings;
  mainBarOpen: boolean;
  mobileMainBarOpen: boolean;
  modalState: Record<string, boolean>;
  isMobile: boolean;
  openMediaPlayer: boolean;
  allLeads: boolean;
  allCalls: boolean;
  floatingActionButton: {
    visible: boolean;
    onClick: (() => void) | null;
    ariaLabel: string;
  };
}

export const useUIStore = defineStore('ui', {
  state: (): UIState => ({
    leadView: 'table',
    tableSettings: {
      campaign: { recordsPerPage: MIN_RECORD },
      lead: { recordsPerPage: MIN_RECORD },
      user: { recordsPerPage: MIN_RECORD },
      call: { recordsPerPage: MIN_RECORD },
      invited: { recordsPerPage: MIN_RECORD }
    },
    mainBarOpen: true,
    mobileMainBarOpen: true,
    modalState: {},
    isMobile: window.innerWidth <= MOBILE_VIEW_THRESHOLD,
    openMediaPlayer: false,
    allLeads: false,
    allCalls: false,
    floatingActionButton: {
      visible: false,
      onClick: null,
      ariaLabel: ''
    }
  }),
  getters: {
    getLeadView: (state: UIState): LeadView => state.leadView,
    getRecordsPerPage:
      state =>
      (tableType: string): number => {
        return state.tableSettings[tableType]?.recordsPerPage;
      },
    isMobileOn: (state: UIState): boolean => state.isMobile,
    isMainBarOpen: state => state.mainBarOpen,
    isMobileSideBarOpen: state => state.mobileMainBarOpen,
    isModalOpen:
      state =>
      (key: string): boolean => {
        return state.modalState[key] || false;
      },
    isMediaPlayerOpen: state => state.openMediaPlayer
  },
  actions: {
    reset() {
      this.$reset();
    },
    setAllLeads(value: boolean) {
      this.allLeads = value;
    },
    setAllCalls(value: boolean) {
      this.allCalls = value;
    },
    setLeadView(view: LeadView) {
      this.leadView = view;
    },
    setRecordsPerPage(tableType: string, recordsPerPage: number) {
      if (this.tableSettings[tableType]) {
        // Use $patch with function for better reactivity and to avoid overwriting
        this.$patch((state) => {
          state.tableSettings[tableType].recordsPerPage = recordsPerPage;
        });
        console.log(`[UI Store] Set ${tableType} recordsPerPage to:`, recordsPerPage);
        console.log('[UI Store] Current tableSettings:', JSON.stringify(this.tableSettings, null, 2));
        
        // Verify it's saved to localStorage
        const persistedKey = '__persisted__ui';
        const saved = localStorage.getItem(persistedKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          console.log('[UI Store] Saved to localStorage:', JSON.stringify(parsed.tableSettings, null, 2));
        }
      }
    },
    toggleSideBar() {
      this.mainBarOpen = !this.mainBarOpen;
    },
    toggleMobileSideBar(value?: boolean) {
      this.mobileMainBarOpen = value ?? !this.mobileMainBarOpen;
    },
    toggleMediaPlayer(value: boolean) {
      this.$patch({ openMediaPlayer: value });
    },
    updateViewport(value: boolean) {
      this.$patch({ isMobile: value });
    },
    setFloatingActionButton(config: {
      visible: boolean;
      onClick: (() => void) | null;
      ariaLabel: string;
    }) {
      this.$patch({
        floatingActionButton: config
      });
    },
    clearFloatingActionButton() {
      this.$patch({
        floatingActionButton: {
          visible: false,
          onClick: null,
          ariaLabel: ''
        }
      });
    }
  },
  persist: true
});
